import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS = {
  pendente: { l: "Aguardando aceite", c: "bg-[#FFCC00]" },
  aceito: { l: "Aceito", c: "bg-[#00A859] text-white" },
  recusado: { l: "Recusado", c: "bg-[#E60000] text-white" },
};

export default function MontadorAditivos() {
  const [lista, setLista] = useState([]);
  const [det, setDet] = useState(null);
  const [recusar, setRecusar] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = async () => {
    const r = await api.get("/montador/aditivos");
    setLista(r.data);
  };
  useEffect(() => { load(); }, []);

  const aceitar = async (a) => {
    if (!window.confirm(`Aceitar aditivo "${a.titulo}"? Registra IP/dispositivo no log jurídico.`)) return;
    try {
      await api.post(`/montador/aditivos/${a.id}/aceitar`);
      toast.success("Aditivo aceito · registrado juridicamente");
      load(); setDet(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarRecusa = async () => {
    if (!motivo.trim()) { toast.error("Informe motivo"); return; }
    try {
      await api.post(`/montador/aditivos/${recusar.id}/recusar`, { motivo });
      toast.success("Recusa registrada");
      setRecusar(null); setMotivo(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pendentes = lista.filter(a => a.status === "pendente");

  return (
    <>
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        {pendentes.length > 0 && (
          <div className="border-l-4 border-[#FFCC00] bg-[#FFCC00]/10 p-4 text-sm">
            <strong>{pendentes.length} aditivo(s) aguardando sua decisão.</strong> Leia cada um cuidadosamente antes de aceitar.
          </div>
        )}

        {lista.length === 0 && (
          <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            Nenhum aditivo recebido.
          </div>
        )}

        {lista.map(a => {
          const st = STATUS[a.status] || STATUS.pendente;
          return (
            <div key={a.id} className="border-2 border-border bg-white p-5" data-testid={`mad-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-cap text-[#142766]">{a.empresa_nome} · v{a.versao}</div>
                  <h2 className="font-heading font-extrabold text-lg mt-1">{a.titulo}</h2>
                  <div className="text-xs text-muted-foreground mt-1">
                    Tipo: <strong>{a.tipo}</strong>
                    {a.vigencia_inicio && <> · Vigência: {a.vigencia_inicio}</>}
                  </div>
                </div>
                <span className={`px-2 py-1 text-[10px] uppercase ${st.c}`}>{st.l}</span>
              </div>

              <div className="text-sm bg-[#F4F4F5] p-3 max-h-32 overflow-hidden text-ellipsis whitespace-pre-wrap">
                {a.conteudo.slice(0, 280)}{a.conteudo.length > 280 && "..."}
              </div>

              <div className="flex gap-2 mt-3">
                <Button onClick={() => setDet(a)} variant="outline" className="btn-sharp flex-1"
                  data-testid={`mad-ver-${a.id}`}>Ler completo</Button>
                {a.status === "pendente" && (
                  <>
                    <Button onClick={() => aceitar(a)} className="btn-sharp brand-bg text-white flex-1"
                      data-testid={`mad-aceitar-${a.id}`}>✓ Aceitar</Button>
                    <Button onClick={() => setRecusar(a)} variant="outline" className="btn-sharp text-[#E60000] border-[#E60000]"
                      data-testid={`mad-recusar-${a.id}`}>Recusar</Button>
                  </>
                )}
              </div>

              {a.aceito_em && (
                <div className="mt-3 text-xs text-[#00A859]">
                  ✓ Aceito em {new Date(a.aceito_em).toLocaleString("pt-BR")}
                </div>
              )}
              {a.recusado_em && (
                <div className="mt-3 text-xs text-[#E60000]">
                  ✗ Recusado: {a.motivo_recusa}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!det} onOpenChange={(o) => !o && setDet(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{det?.titulo}</DialogTitle></DialogHeader>
          {det && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                {det.empresa_nome} · v{det.versao} · {det.tipo}
              </div>
              <div className="border border-border p-4 text-sm whitespace-pre-wrap max-h-[60vh] overflow-y-auto">
                {det.conteudo}
              </div>
            </div>
          )}
          <DialogFooter>
            {det?.status === "pendente" && (
              <>
                <Button variant="outline" onClick={() => { setRecusar(det); setDet(null); }}
                  className="text-[#E60000] border-[#E60000]" data-testid="mad-dlg-recusar">Recusar</Button>
                <Button onClick={() => aceitar(det)} className="brand-bg text-white"
                  data-testid="mad-dlg-aceitar">✓ Aceitar</Button>
              </>
            )}
            {det?.status !== "pendente" && <Button onClick={() => setDet(null)}>Fechar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recusar} onOpenChange={(o) => !o && setRecusar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Recusar aditivo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{recusar?.titulo}</div>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo da recusa (será enviado à empresa)" data-testid="mad-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecusar(null)}>Cancelar</Button>
            <Button onClick={enviarRecusa} className="bg-[#E60000] text-white" data-testid="mad-recusa-conf">
              Confirmar recusa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
